"use client"
import { useRouter } from "next/navigation"
import {
	MouseEventHandler,
	KeyboardEvent as ReactKeyboardEvent,
	useCallback,
	useEffect,
	useRef,
} from "react"
import { useLockedBodyScroll } from "@/lib/lockBodyScroll"
import * as styles from "./Modal.css"

type ModalProps = {
	children: React.ReactNode
	ariaLabel?: string
	ariaLabelledBy?: string
	viewTransitions?: "default" | "none"
	isOpen?: boolean
	onDismiss?: (() => void) | "back"
}
export default function Modal({
	children,
	ariaLabel,
	ariaLabelledBy,
	viewTransitions,
	isOpen = true,
	onDismiss: onDismissProp = () => {},
}: ModalProps) {
	const dialog = useRef<HTMLDialogElement>(null)
	const wrapper = useRef<HTMLDivElement>(null)

	const router = useRouter()

	const onDismiss = useCallback(() => {
		if (onDismissProp === "back") {
			router.back()
		} else {
			onDismissProp()
		}
	}, [onDismissProp, router])

	const onOverlayClick: MouseEventHandler<HTMLElement> = useCallback(
		(event) => {
			if (event.target === dialog.current || event.target === wrapper.current) {
				onDismiss()
			}
		},
		[onDismiss],
	)

	const onKeyDown = useCallback(
		(event: KeyboardEvent | ReactKeyboardEvent) => {
			if (event.key === "Escape") onDismiss()
		},
		[onDismiss],
	)

	useEffect(() => {
		document.addEventListener("keydown", onKeyDown)
		return () => {
			document.removeEventListener("keydown", onKeyDown)
		}
	}, [onKeyDown])

	useLockedBodyScroll()

	return (
		<dialog
			open={isOpen}
			ref={dialog}
			className={styles.dialog}
			onClick={onOverlayClick}
			onKeyDown={onKeyDown}
			aria-label={ariaLabel}
			aria-labelledby={ariaLabelledBy}
			aria-modal
		>
			<div ref={wrapper} className={styles.content({ viewTransitions })}>
				{children}
			</div>
		</dialog>
	)
}
